import type { FormEvent } from 'react'
import type { DateRange } from 'react-day-picker'

import { format, isValid, parse } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type { Holiday, HolidayDto, ListHolidaysParams } from '@/utils/api/leave-service/models'

import { OrganisationSearchInput } from '@/components/common/OrganisationSearchInput'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AnimatedSwitch } from '@/components/ui/animated-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import { useDeleteHoliday, useListHolidays, useSaveHoliday, useUpdateHoliday } from '@/utils/api/leave-service'
import { useGetAllOrganisations } from '@/utils/api/user-service'

const DEFAULT_FORM_STATE = {
  id: undefined as number | undefined,
  holidayDate: '',
  name: '',
  workingDayOverride: false,
}

export function HolidaysPage() {
  const { user: currentUser } = useAuth()
  const showOrganisationFilter = isAdminUser(currentUser)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [filters, setFilters] = useState({ from: '', to: '' })
  const [organisationFilter, setOrganisationFilter] = useState('all')
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE)
  const listParams = useMemo<ListHolidaysParams>(() => ({
    from: filters.from.trim() === '' ? undefined : filters.from,
    to: filters.to.trim() === '' ? undefined : filters.to,
    pageable: { page: 0, size: 25 },
  }), [filters.from, filters.to])

  const listQuery = useListHolidays(listParams)
  const organisationsQuery = useGetAllOrganisations({
    query: {
      enabled: showOrganisationFilter,
      staleTime: 30_000,
    },
  })
  const saveMutation = useSaveHoliday({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogMode('create')
        setDialogOpen(false)
        void listQuery.refetch()
      },
    },
  })
  const updateMutation = useUpdateHoliday({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogMode('create')
        setDialogOpen(false)
        void listQuery.refetch()
      },
    },
  })
  const deleteMutation = useDeleteHoliday({
    mutation: {
      onSuccess: () => {
        void listQuery.refetch()
      },
    },
  })

  const holidays = useMemo(() => listQuery.data?.data?.content ?? [], [listQuery.data])
  const organisations = useMemo(() => organisationsQuery.data?.data ?? [], [organisationsQuery.data])
  const organisationFilterOptions = useMemo(() => [
    { id: 'all', value: 'all', name: 'All organisations' },
    ...organisations
      .filter(org => typeof org.id === 'number')
      .map(org => ({
        id: String(org.id),
        value: String(org.id),
        name: org.name ?? `Organisation ${org.id}`,
      })),
  ], [organisations])
  const canSubmit = formState.holidayDate.trim() !== '' && formState.name.trim() !== ''
  const isSaving = saveMutation.isPending || updateMutation.isPending
  const filteredHolidays = useMemo(() => {
    if (!showOrganisationFilter || organisationFilter === 'all') {
      return holidays
    }
    const parsedOrganisationId = Number(organisationFilter)
    if (Number.isNaN(parsedOrganisationId)) {
      return holidays
    }
    return holidays.filter(holiday => holiday.organisationId === parsedOrganisationId)
  }, [holidays, organisationFilter, showOrganisationFilter])

  const selectedRange = useMemo<DateRange | undefined>(() => {
    const trimmedFrom = filters.from.trim()
    const trimmedTo = filters.to.trim()
    const parsedFrom = trimmedFrom ? parse(trimmedFrom, 'yyyy-MM-dd', new Date()) : undefined
    const parsedTo = trimmedTo ? parse(trimmedTo, 'yyyy-MM-dd', new Date()) : undefined
    const startDate = parsedFrom && isValid(parsedFrom) ? parsedFrom : undefined
    const endDate = parsedTo && isValid(parsedTo) ? parsedTo : undefined
    return startDate ? { from: startDate, to: endDate } : undefined
  }, [filters.from, filters.to])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || isSaving) {
      return
    }

    const payload: HolidayDto = {
      holidayDate: formState.holidayDate.trim(),
      name: formState.name.trim(),
      workingDayOverride: formState.workingDayOverride,
    }

    if (dialogMode === 'edit' && typeof formState.id === 'number') {
      updateMutation.mutate({ id: formState.id, data: payload })
      return
    }

    saveMutation.mutate({ data: payload })
  }

  const openCreateDialog = useCallback(() => {
    setFormState(DEFAULT_FORM_STATE)
    setDialogMode('create')
    setDialogOpen(true)
  }, [])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setFormState(DEFAULT_FORM_STATE)
      setDialogMode('create')
    }
  }, [])

  const handleDateRangeSelection = useCallback((range: DateRange | undefined) => {
    setFilters({
      from: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
      to: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
    })
  }, [])

  const handleEdit = useCallback((item: Holiday) => {
    setFormState({
      id: item.id,
      holidayDate: item.holidayDate ?? '',
      name: item.name ?? '',
      workingDayOverride: item.workingDayOverride ?? false,
    })
    setDialogMode('edit')
    setDialogOpen(true)
  }, [])

  const handleDelete = useCallback((item: Holiday) => {
    if (!item.id || deleteMutation.isPending) {
      return
    }
    deleteMutation.mutate({ params: { id: item.id } })
  }, [deleteMutation])

  const columns = useMemo<ColumnConfig<Holiday>[]>(() => ([
    {
      key: 'holidayDate',
      header: 'Date',
      type: 'date',
      cell: value => value ? format(new Date(value as string | number | Date), 'do MMMM') : 'N/A',
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'workingDayOverride',
      header: 'Working Day',
      type: 'boolean',
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={!row.id || deleteMutation.isPending}
            onClick={() => handleDelete(row)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ]), [deleteMutation.isPending, handleDelete, handleEdit])

  const dialogTitle = dialogMode === 'edit' ? 'Edit Holiday' : 'Create Holiday'
  const dialogDescription = dialogMode === 'edit'
    ? 'Update the selected holiday or working day override.'
    : 'Add a new holiday or working day override.'
  const submitLabel = dialogMode === 'edit' ? 'Save Changes' : 'Create Holiday'
  const submitError = dialogMode === 'edit' ? updateMutation.error : saveMutation.error
  const hasSubmitError = dialogMode === 'edit' ? updateMutation.isError : saveMutation.isError

  return (
    <div className="space-y-6">
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="holiday-date">Holiday Date</Label>
                <Input
                  id="holiday-date"
                  type="date"
                  value={formState.holidayDate}
                  onChange={event => setFormState(prev => ({ ...prev, holidayDate: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="holiday-name">Holiday Name</Label>
                <Input
                  id="holiday-name"
                  value={formState.name}
                  onChange={event => setFormState(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="Public Holiday"
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border px-4 py-3 md:col-span-2">
                <div>
                  <Label htmlFor="holiday-working-override">Working Day Override</Label>
                  <p className="text-sm text-muted-foreground">Treat as a working day.</p>
                </div>
                <AnimatedSwitch
                  id="holiday-working-override"
                  checked={formState.workingDayOverride}
                  onCheckedChange={checked => setFormState(prev => ({ ...prev, workingDayOverride: checked }))}
                />
              </div>
            </div>
            {hasSubmitError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to save holiday</AlertTitle>
                  <AlertDescription>{submitError?.message ?? 'Please try again.'}</AlertDescription>
                </Alert>
              )
              : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || isSaving}>
                {isSaving ? 'Saving...' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Holiday Calendar</CardTitle>
            <CardDescription>Filter and manage saved holidays.</CardDescription>
          </div>
          <Button type="button" onClick={openCreateDialog}>
            Create Holiday
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {listQuery.isPending
            ? (
              <p className="text-sm text-muted-foreground">Loading holidays...</p>
            )
            : null}
          {listQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load holidays</AlertTitle>
                <AlertDescription>{listQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {showOrganisationFilter && organisationsQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load organisations</AlertTitle>
                <AlertDescription>{organisationsQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {deleteMutation.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to delete holiday</AlertTitle>
                <AlertDescription>{deleteMutation.error?.message ?? 'Please try again.'}</AlertDescription>
              </Alert>
            )
            : null}
          <EnhancedDataTable
            data={filteredHolidays}
            columns={columns}
            loading={listQuery.isPending}
            emptyStateMessage="No holidays found."
            enableColumnFilters={false}
            enableExport={false}
            customButtons={(
              <div className="flex flex-wrap items-center gap-2">
                {showOrganisationFilter
                  ? (
                    <OrganisationSearchInput
                      id="holiday-organisation-filter"
                      label="Organisation"
                      placeholder={organisationsQuery.isPending ? 'Loading organisations...' : 'All organisations'}
                      value={organisationFilter}
                      onValueChange={setOrganisationFilter}
                      options={organisationFilterOptions}
                      isLoading={organisationsQuery.isPending}
                      disabled={organisationsQuery.isPending || organisationsQuery.isError}
                    />
                  )
                  : null}
                <DateRangePicker
                  id="holiday-filter-range"
                  selected={selectedRange}
                  onSelect={handleDateRangeSelection}
                  displayFormat="MMM d"
                  numberOfMonths={2}
                  captionLayout="dropdown"
                  className="w-[220px]"
                />
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  )
}
